/**
 * Equipment resolve chain (E2.2.S2) — turn an equipped `ItemWire` into the
 * asset-pack + model the renderer should instantiate on the avatar.
 *
 * The chain (all existing `presentation.assetPack`/`model` grammar — no new
 * content field, no validator touch, nothing on the wire):
 *
 *   equipped ItemWire
 *     → contentId
 *       → client-pack items[contentId].presentation  (assetPack + model)
 *         → pack-qualified asset-packs[assetPack]     (the AssetPackDef manifest)
 *           → AssetLoader.instantiate(model, lod0)    (done by the renderer)
 *
 * Every unresolvable link returns `null` so the caller simply renders no
 * attachment (the loader's own null path handles an unknown `model` inside a
 * known pack, and a load error). This is the graceful-fallback contract: the
 * three deliberately-unbound slice items (tinker-lantern, conduit-lantern,
 * pathmark-charm — no `presentation`) resolve to `null` and attach nothing.
 *
 * Pure + node-safe (types only from the client surface). Pack-membership of
 * `model` is NOT asserted here — since AD-413 `validate.ts` checks it on every
 * definition (the E2.2 "free string, never validate.ts" rule is superseded); an
 * out-of-pack model that slips past staging still degrades to no-attachment at
 * the loader. The 18-binding resolve-chain proof lives in the test suite.
 *
 * Import boundary (AD-122): shared types via `@great-cloud/shared/client` only.
 */

import type {
  AssetPackDef,
  CosmeticDef,
  ItemWire,
  PresentationRef,
} from '@great-cloud/shared/client';

/**
 * How the renderer reads presentation data — the client content pack's `items`
 * and `asset-packs` projections. Built in App from the loaded `ContentPackStore`
 * (`get('items', id)` / `get('asset-packs', id)`); a small test fake satisfies
 * it too. Kept separate from the scene's `SceneContentLookup` (theme/assetPack)
 * so this feature touches no other module's contract.
 */
export interface EquipmentContent {
  /** Client-pack `items[contentId].presentation`, or undefined if absent/unbound. */
  itemPresentation(contentId: string): PresentationRef | undefined;
  /** Client-pack `asset-packs[id]` (its `assets[]` manifest), or undefined if not delivered. */
  assetPack(id: string): AssetPackDef | undefined;
  /**
   * Client-pack `cosmetics[id]` projection (`kind`/`slot`/`appliesTo`/`presentation`
   * — client-pack.ts PRESENTATION_FIELDS), or undefined when unknown. Optional:
   * a content source without it simply never resolves a skin (weapon-skins.md).
   */
  cosmetic?(id: string): ClientCosmetic | undefined;
}

/** The cosmetic fields the skin overlay reads (the client-pack projection of `CosmeticDef`). */
export type ClientCosmetic = Pick<CosmeticDef, 'kind' | 'slot' | 'appliesTo' | 'presentation'>;

/**
 * The player's client-local skin preference: the preferred cosmetic id for a
 * slot, or `null` for "the item's own base". Self-view only — sourced from
 * `skinPreference.ts` (localStorage + the dev bridge), NEVER from the wire.
 */
export type SkinPreference = (slot: string) => string | null;

/** A resolved equipment attachment: everything the renderer needs to place a mesh. */
export interface EquipmentResolution {
  /** The equipment slot to key/attach at (the item's `slot` — the socket name). */
  slot: string;
  /** The resolved asset-pack id (`presentation.assetPack`). */
  assetPack: string;
  /** The asset id within the pack to instantiate at lod0 (`presentation.model`). */
  model: string;
  /** The resolved pack def — construct an `AssetManifest`/`AssetLoader` from it. */
  pack: AssetPackDef;
  /**
   * The archetype whose grip/socket calibration the mesh is placed with — the
   * item's own bound model. Equals `model` unless a skin overlay applied, in
   * which case it is the skin's `appliesTo.archetype` (the skin mesh is fitted to
   * that frame, so the base's rig socket offsets apply verbatim).
   */
  gripModel: string;
  /** The cosmetic id when a weapon-skin overlay resolved; absent for the base mesh. */
  skin?: string;
}

/** The equipped-item fields the resolve chain reads (a slice of `ItemWire`). */
export type EquippableItem = Pick<ItemWire, 'contentId' | 'slot'>;

/**
 * Resolve an equipped item to its pack + model, or `null` for any graceful
 * fallback (no slot, no presentation binding, or a pack that isn't delivered).
 * The final `instantiate(model, lod0)` — and its own null fallback for an
 * unknown model / load error — is the renderer's/loader's job.
 *
 * Skin overlay (docs/systems/weapon-skins.md): when `skinFor` names a cosmetic
 * for the item's slot, and that cosmetic is a `weapon-skin` whose
 * `appliesTo.archetype` equals the item's bound model, and its own presentation
 * resolves to a delivered pack, the skin's pack + model are returned instead —
 * with `gripModel` still the base archetype. Every other case (unknown id, wrong
 * kind, archetype mismatch, undelivered skin pack) falls through to the base
 * mesh: a bad preference can never blank the weapon.
 */
export function resolveEquipment(
  content: EquipmentContent,
  item: EquippableItem,
  skinFor?: SkinPreference,
): EquipmentResolution | null {
  const slot = item.slot;
  if (slot == null || slot === '') return null; // can't place without a slot key
  const presentation = content.itemPresentation(item.contentId);
  const assetPack = presentation?.assetPack;
  const model = presentation?.model;
  if (assetPack === undefined || assetPack === '' || model === undefined || model === '') {
    return null; // no binding ⇒ no attachment (the deliberately-unbound items land here)
  }
  const gripModel = gripArchetype(presentation);
  const skin = resolveSkin(content, slot, gripModel ?? model, skinFor);
  if (skin !== null) return skin;
  const pack = content.assetPack(assetPack);
  if (pack === undefined) return null; // pack not delivered ⇒ no attachment
  return { slot, assetPack, model, pack, gripModel: gripModel ?? model };
}

/**
 * The archetype an item is held/worn as: `presentation.archetype` when a per-item
 * mesh names one, else its bound `model` (a shared base is its own archetype).
 * Sockets, grip offsets and the attack class all key on this, never on a
 * per-item mesh name. `undefined` when the item binds no model.
 */
export function gripArchetype(presentation: PresentationRef | undefined): string | undefined {
  const archetype = presentation?.archetype;
  if (archetype !== undefined && archetype !== '') return archetype;
  const model = presentation?.model;
  return model === undefined || model === '' ? undefined : model;
}

/** The skin overlay half of `resolveEquipment`; `null` ⇒ use the base mesh. */
function resolveSkin(
  content: EquipmentContent,
  slot: string,
  archetype: string,
  skinFor: SkinPreference | undefined,
): EquipmentResolution | null {
  if (skinFor === undefined || content.cosmetic === undefined) return null;
  const cosmeticId = skinFor(slot);
  if (cosmeticId === null || cosmeticId === '') return null;
  const cosmetic = content.cosmetic(cosmeticId);
  if (cosmetic === undefined || cosmetic.kind !== 'weapon-skin') return null;
  if (cosmetic.appliesTo?.archetype !== archetype) return null; // a sword skin never dresses a staff
  const skinPack = cosmetic.presentation?.assetPack;
  const skinModel = cosmetic.presentation?.model;
  if (skinPack === undefined || skinPack === '' || skinModel === undefined || skinModel === '') {
    return null;
  }
  const pack = content.assetPack(skinPack);
  if (pack === undefined) return null; // skin pack not delivered ⇒ base mesh
  return {
    slot,
    assetPack: skinPack,
    model: skinModel,
    pack,
    gripModel: archetype,
    skin: cosmeticId,
  };
}
